/**
 * ListFooter — `.list-foot` from the prototype (cms-redesign-mockup.html
 * ~line 929–1003). Pagination footer for server-paginated list pages.
 *
 *   <ListFooter
 *     page={1}
 *     perPage={50}
 *     totalCount={247}
 *     pageCount={5}
 *     onPageChange={setPage}
 *     onPerPageChange={setPerPage}
 *     itemLabel="sessions"
 *   />
 *
 * Behaviour:
 *   • Left      — "Showing 1–50 of 247 sessions"
 *   • Center    — prev | numbered pages with ellipses | next
 *   • Right     — Per-page selector (25/50/100)
 *
 * The numbered pager renders at most 7 buttons: 1, [ellipsis], current −1,
 * current, current + 1, [ellipsis], pageCount. Edges always shown so users
 * can jump to first/last without scrubbing.
 */
import React from 'react';
import { cn } from './cn';

const PER_PAGE_OPTIONS = [25, 50, 100, 200];

/** Build the array of page tokens (numbers + 'ellipsis' markers) to render. */
function buildPageTokens(current, pageCount) {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const tokens = [1];
  // Window around current
  const start = Math.max(2, current - 1);
  const end   = Math.min(pageCount - 1, current + 1);
  if (start > 2) tokens.push('ellipsis-left');
  for (let p = start; p <= end; p++) tokens.push(p);
  if (end < pageCount - 1) tokens.push('ellipsis-right');
  tokens.push(pageCount);
  return tokens;
}

export default function ListFooter({
  page = 1,
  perPage = 50,
  totalCount = 0,
  pageCount = 0,
  onPageChange,
  onPerPageChange,
  itemLabel = 'items',
  className,
}) {
  const safePageCount = Math.max(1, pageCount || 1);
  const safePage = Math.min(Math.max(1, page), safePageCount);
  const from = totalCount === 0 ? 0 : (safePage - 1) * perPage + 1;
  const to   = Math.min(safePage * perPage, totalCount);

  const tokens = buildPageTokens(safePage, safePageCount);
  const goTo = (p) => {
    if (!onPageChange) return;
    const clamped = Math.min(Math.max(1, p), safePageCount);
    if (clamped !== safePage) onPageChange(clamped);
  };

  return (
    // `.list-foot` (930–937).
    <div
      className={cn(
        'flex items-center justify-between bg-surface border-t border-sand shrink-0',
        className,
      )}
      style={{ padding: '11px 28px', gap: 16 }}
    >
      {/* `.lf-info` (938–942) */}
      <div className="text-text-light shrink-0" style={{ fontSize: 12.5 }}>
        Showing{' '}
        <strong className="text-navy font-bold">
          {totalCount === 0 ? '0' : `${from.toLocaleString()}–${to.toLocaleString()}`}
        </strong>{' '}
        of{' '}
        <strong className="text-navy font-bold">
          {totalCount.toLocaleString()}
        </strong>{' '}
        {itemLabel}
      </div>

      {/* `.lf-pager` (943–945) */}
      <div className="flex items-center" style={{ gap: 4 }}>
        <PageBtn
          disabled={safePage <= 1}
          onClick={() => goTo(safePage - 1)}
          aria-label="Previous page"
        >
          ‹
        </PageBtn>
        {tokens.map((t, i) =>
          t === 'ellipsis-left' || t === 'ellipsis-right' ? (
            <span
              key={t + i}
              aria-hidden
              className="font-mono text-text-muted select-none"
              style={{ padding: '0 4px', fontSize: 12.5 }}
            >
              …
            </span>
          ) : (
            <PageBtn
              key={t}
              active={t === safePage}
              onClick={() => goTo(t)}
              aria-label={`Page ${t}`}
              aria-current={t === safePage ? 'page' : undefined}
            >
              {t}
            </PageBtn>
          ),
        )}
        <PageBtn
          disabled={safePage >= safePageCount}
          onClick={() => goTo(safePage + 1)}
          aria-label="Next page"
        >
          ›
        </PageBtn>
      </div>

      {/* `.lf-per` (971–993) — per-page selector. */}
      <label
        className="flex items-center text-text-light shrink-0"
        style={{ gap: 8, fontSize: 12.5 }}
      >
        <span>Per page</span>
        <select
          value={perPage}
          onChange={(e) => onPerPageChange?.(Number(e.target.value))}
          className={cn(
            'cursor-pointer border border-sand bg-surface text-text',
            'focus:outline-none focus:border-teal-green',
          )}
          style={{
            height: 30,
            padding: '0 22px 0 8px',
            borderRadius: 8,
            fontSize: 12.5,
            appearance: 'none',
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none' stroke='%2375817C' stroke-width='1.6' stroke-linecap='round'><polyline points='3 5 6 8 9 5'/></svg>\")",
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 6px center',
            backgroundSize: '12px 12px',
          }}
        >
          {PER_PAGE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </label>
    </div>
  );
}

/** `.pg-btn` (946–965). */
function PageBtn({ children, active, disabled, ...rest }) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center font-mono font-semibold',
        'border border-transparent rounded-md cursor-pointer',
        'transition-all duration-150',
        disabled && 'opacity-[0.35] cursor-not-allowed',
        active
          ? 'bg-navy text-bg'
          : 'bg-transparent text-text hover:bg-cream hover:text-navy',
      )}
      style={{
        minWidth: 30,
        height: 30,
        padding: '0 8px',
        fontSize: 12.5,
        ...(active && { boxShadow: '0 1px 3px #2C3E5026' }),
      }}
      {...rest}
    >
      {children}
    </button>
  );
}
